import {
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { ChartTooltipShell } from "../panel";
import { DEVELOPMENT_AGES, ULTIMATE_AGE } from "../../../pages/AthenaPricingEngine/lib/api/calibration";
import type { LossDevelopmentPoint } from "../../../pages/AthenaPricingEngine/lib/api/calibration";

const AXIS_COLOR = "var(--neutral-inactive)";

/** One distinct colour per vintage. Deliberately excludes
 *  `--neutral-inactive` (the axis colour, too low-contrast to read as a data
 *  line) and holds enough entries that a normal 2021-2026 range never wraps
 *  onto a duplicate. */
const VINTAGE_RAMP = [
  "var(--grade-a)",
  "var(--grade-b1)",
  "var(--grade-b2)",
  "var(--grade-b3)",
  "var(--decline)",
  "var(--amber)",
  // hsl(...) is required: --primary is a Tailwind HSL TRIPLET, not a colour, so a
  // bare var(--primary) in a stroke paints black. Every other entry here is already
  // a complete hsl(...) token, which is why only the 7th vintage was affected.
  "hsl(var(--primary))",
];

/** Colour is keyed to the vintage's position in the FULL range, not the
 *  filtered one, so a vintage keeps its colour when the funded-year toggle
 *  narrows the chart -- otherwise selecting a single year re-indexed it to
 *  whatever sat first in the ramp. */
function colorFor(vintage: number, allVintages: number[]) {
  const index = allVintages.indexOf(vintage);
  return VINTAGE_RAMP[(index < 0 ? 0 : index) % VINTAGE_RAMP.length]!;
}

// The two dotted series have to be told apart at a glance: the vintage's own
// curve past maturity, and the full-range projection. Same colour by design
// (they belong to one vintage), so the dash pattern is the only thing carrying
// the distinction -- fine dots for the curve, larger and wider-spaced for the
// projection.
const CHART_DASH = "1 3";
const PROJECTED_DASH = "4 7";

function ageLabel(age: number) {
  return age >= ULTIMATE_AGE ? "ultimate" : String(age);
}

function DevelopmentTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{ name: string; value: number | null; color: string }>;
  label?: string;
}) {
  if (!active || !payload?.length) return null;
  // The vintage's curve is drawn as two series (realized + dotted
  // continuation) that share a name, and they overlap by one point at the
  // maturity boundary -- the bridge that joins them. Without this the tooltip
  // lists that age twice, once under each half.
  const seen = new Set<string>();
  const entries = payload.filter(
    (entry): entry is { name: string; value: number; color: string } => {
      if (entry.value == null || seen.has(entry.name)) return false;
      seen.add(entry.name);
      return true;
    },
  );
  if (!entries.length) return null;
  return (
    <ChartTooltipShell>
      <p className="font-semibold text-foreground">Age {label}</p>
      <div className="mt-1 space-y-0.5">
        {entries.map((entry) => (
          <p
            key={entry.name}
            className="flex items-center gap-2 text-muted-foreground"
          >
            <span className="size-2" style={{ backgroundColor: entry.color }} aria-hidden />
            {entry.name}
            <span className="ml-auto tabular-nums text-foreground">
              {entry.value.toFixed(2)}%
            </span>
          </p>
        ))}
      </div>
    </ChartTooltipShell>
  );
}

export function LossDevelopmentChart({
  points,
  allVintages,
  height = 340,
  showProjected = true,
}: {
  points: LossDevelopmentPoint[];
  /** Every vintage in the unfiltered data, so colours stay put when the
   *  funded-year toggle narrows `points`. Falls back to whatever `points`
   *  contains when omitted. */
  allVintages?: number[];
  height?: number;
  /** false hides the always-drawn v{vintage}p backtest line. Loss
   *  Calibration's Realized basis sets this -- pre-filtering `points` to
   *  drop every isProjected point empties the solid/continuation series on
   *  its own, but this third line reads `projectedLossPct`, which is
   *  populated at every age regardless of isProjected, so it needs its own
   *  switch. */
  showProjected?: boolean;
}) {
  const vintages = Array.from(new Set(points.map((p) => p.vintage))).sort((a, b) => a - b);
  const palette = allVintages?.length ? allVintages : vintages;
  // Recent vintages can carry byte-identical projections (2025 and 2026 match
  // exactly on every age for several industries), so their dotted lines sit
  // perfectly on top of each other. Nothing can separate identical geometry --
  // drawing the newest last and slightly heavier at least makes it the one
  // that reads on top rather than being hidden under its neighbour.
  const newest = vintages[vintages.length - 1];
  const widthFor = (vintage: number) => (vintage === newest ? 2.75 : 1.75);

  // Each vintage's curve spans every age; `isProjected` only decides which
  // half of it a given age belongs to. Splitting it across two series is how
  // one line gets two dash styles -- `v{y}` carries the realized stretch,
  // `v{y}c` the unrealized continuation. The separate `v{y}p` projection line
  // is independent of both.
  const data = DEVELOPMENT_AGES.map((age) => {
    const row: Record<string, number | string | null> = { age: ageLabel(age) };
    for (const vintage of vintages) {
      const point = points.find((p) => p.vintage === vintage && p.ageMonths === age);
      const value = point?.chartLossPct ?? null;
      row[`v${vintage}`] = point && !point.isProjected ? value : null;
      row[`v${vintage}c`] = point && point.isProjected ? value : null;
      row[`v${vintage}p`] = point?.projectedLossPct ?? null;
    }
    return row;
  });

  // Bridge: repeat the last realized reading on the continuation series, so
  // the dotted stretch starts where the solid one ended instead of leaving a
  // visible gap at the maturity boundary.
  for (const vintage of vintages) {
    const lastRealized = points
      .filter((p) => p.vintage === vintage && !p.isProjected && p.chartLossPct != null)
      .sort((a, b) => b.ageMonths - a.ageMonths)[0];
    if (!lastRealized) continue;
    const index = DEVELOPMENT_AGES.indexOf(
      lastRealized.ageMonths as (typeof DEVELOPMENT_AGES)[number],
    );
    if (index >= 0 && data[index]) {
      data[index]![`v${vintage}c`] = lastRealized.chartLossPct;
    }
  }

  return (
    <div className="w-full">
      <div style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        {/* bottom margin leaves room for the axis title -- same pairing with
            the label's offset that actual-vs-target-chart.tsx uses. */}
        <ComposedChart data={data} margin={{ top: 8, right: 12, bottom: 24, left: 0 }}>
          <CartesianGrid stroke="var(--border)" strokeDasharray="2 4" vertical={false} />
          <XAxis
            dataKey="age"
            tick={{ fill: AXIS_COLOR, fontSize: 11 }}
            stroke={AXIS_COLOR}
            tickLine={false}
            label={{
              value: "Age (Months Since Origination)",
              position: "insideBottom",
              offset: -14,
              fill: AXIS_COLOR,
              fontSize: 11,
            }}
          />
          <YAxis
            tickFormatter={(value: number) => `${value.toFixed(1)}%`}
            tick={{ fill: AXIS_COLOR, fontSize: 11 }}
            stroke={AXIS_COLOR}
            width={56}
          />
          <Tooltip
            content={<DevelopmentTooltip />}
            contentStyle={{
              background: "var(--popover)",
              border: "1px solid var(--border)",
              borderRadius: 0,
            }}
          />
          {vintages.map((vintage) => (
            <Line
              key={`v${vintage}`}
              type="monotone"
              dataKey={`v${vintage}`}
              // "2023 actual", to sit against "2023 projected" below. Without the
              // word the tooltip listed a bare "2023" next to "2023 projected",
              // leaving the reader to infer that the unlabelled one was the actual.
              name={`${vintage} actual`}
              stroke={colorFor(vintage, palette)}
              strokeWidth={widthFor(vintage)}
              // A vintage with only ONE realized age (2024 has just 12m; 2025
              // and 2026 have none) draws no line segment at all, so without a
              // marker its realized data is invisible and the vintage looks
              // dropped. The dot is what makes that single reading show up.
              dot={{ r: 2.5 }}
              connectNulls={false}
              isAnimationActive={false}
            />
          ))}
          {vintages.map((vintage) => (
            <Line
              key={`v${vintage}c`}
              type="monotone"
              dataKey={`v${vintage}c`}
              // Same name as the realized half: they are one curve, so the
              // tooltip should report a single "2023 actual" reading per age (the
              // dedupe in DevelopmentTooltip collapses the shared bridge point).
              // These two MUST stay identical -- the dedupe keys on the name.
              name={`${vintage} actual`}
              stroke={colorFor(vintage, palette)}
              strokeWidth={widthFor(vintage)}
              strokeDasharray={CHART_DASH}
              strokeLinecap="round"
              dot={false}
              connectNulls={false}
              isAnimationActive={false}
            />
          ))}
          {showProjected &&
            vintages.map((vintage) => (
              <Line
                key={`v${vintage}p`}
                type="monotone"
                dataKey={`v${vintage}p`}
                name={`${vintage} projected`}
                stroke={colorFor(vintage, palette)}
                // Deliberately heavier and more widely spaced than the curve's own
                // dotted stretch above, which uses "1 3" at the base width. Both
                // lines are dotted in the same vintage colour, so with identical
                // dash patterns they were indistinguishable wherever they ran near
                // each other -- and past maturity they run over the same ages. The
                // round linecap turns each dash into a dot, so a longer dash is
                // literally a bigger dot.
                strokeWidth={widthFor(vintage) + 0.75}
                strokeDasharray={PROJECTED_DASH}
                strokeLinecap="round"
                dot={false}
                connectNulls={false}
                isAnimationActive={false}
              />
            ))}
        </ComposedChart>
      </ResponsiveContainer>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
        {vintages.map((vintage) => (
          <span key={vintage} className="flex items-center gap-1.5">
            <span
              className="inline-block h-0.5 w-4"
              style={{ backgroundColor: colorFor(vintage, palette) }}
              aria-hidden
            />
            {vintage}
          </span>
        ))}
        <span className="ml-auto">
          {showProjected ? "Solid = actual · dotted = projected" : "Solid = actual"}
        </span>
      </div>
    </div>
  );
}
